/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* See LICENSE.md in the project root for license terms and full copyright notice.
*--------------------------------------------------------------------------------------------*/

import { SourceFile, Annotation } from "../../types";
import { AnnotationLocationParser, FileLocation } from "./parsers/AnnotationLocationParser";
import { WalkthroughStep } from "./parsers/AnnotationParser";

export interface WalkthroughLoaderOptions {
  start: RegExp;
  end: RegExp;
  identifier: RegExp;
}

export const defaultLoaderOptions: WalkthroughLoaderOptions = {
  start: /START\s*[A-Z0-9_-]+/,
  end: /END\s*[A-Z0-9_-]+/,
  identifier: /(?:START|END)\s*([A-Z0-9_-]+)/,
};

export class WalkthroughLoader {

  public static createWalkthroughAnnotations(walkthroughSteps: WalkthroughStep[], files: SourceFile[], options: WalkthroughLoaderOptions = defaultLoaderOptions) {

    files = files.filter((file) => file.content);

    const fileLocations: { file: string, locations: FileLocation[] }[] = [];
    files.forEach((file) => {
      const locations = AnnotationLocationParser.parseAnnotationLocations(file.content!, options);
      fileLocations.push({ file: file.filePathRenamed ?? file.filePath, locations });
    });

    const walkthrough: Annotation[] = [];

    let currentStep = 0;
    let minorStep = 0.1;
    let prevMarkdown = "";
    walkthroughSteps.forEach((annotation) => {
      let index = annotation.index;
      if (index === undefined) {
        index = currentStep;
        if (annotation.minor) {
          index += minorStep;
          minorStep += 0.1;
        } else {
          minorStep = 0.1;
          index++;
        }
      }

      let markdown = annotation.markdown;
      if (markdown.length) {
        prevMarkdown = annotation.markdown;
      } else {
        markdown = prevMarkdown;
      }

      const step: Annotation = {
        index: index.toString(),
        id: annotation.id,
        title: annotation.title!,
        markdown,
        skip: annotation.minor,
      };

      if (annotation.id !== "NONE") {
        const fileLocation = fileLocations.find((fileLoc) => fileLoc.locations.some((loc) => loc.id === annotation.id));
        if (fileLocation) {
          const location = fileLocation.locations.find((loc) => loc.id === annotation.id);
          if (location) {
            step.startLineNumber = location.start;
            step.endLineNumber = location.end;
            step.filePath = fileLocation.file;
          }
        }
      }

      const walkthroughIndex = walkthrough.findIndex((anno) => anno.index === step.index);
      if (walkthroughIndex > -1) {
        walkthrough.splice(walkthroughIndex, 1, step);
      } else {
        walkthrough.push(step);
      }
      currentStep = Math.floor(index);
    });

    return walkthrough.sort(sort);
  }
}

function sort(a: Annotation, b: Annotation) {
  if (parseInt(a.index, 10) < parseInt(b.index, 10)) {
    return -1;
  } else if (a.index > b.index) {
    return 1;
  }
  return 0;
}
